import React from "react";
import Table from "./Table";
import NCS_REF from "../data/NCS_REF.json";
import { SensoryRowType } from "../types/NCS_EMG";

interface SensoryTableProps {
  rows: SensoryRowType[];
  setRows: React.Dispatch<React.SetStateAction<SensoryRowType[]>>;
  nerveOptions: string[];
  recOptions: string[];
  stimOptions: string[];
  sideOptions: string[];
}

const nerveGroups: Record<string, string[]> = {
  UEx: [
    "Median",
    "Ulnar",
    "Superficial radial",
    "Dorsal ulnar cutaneous",
    "MABC",
    "LABC",
  ],
  LEx: [
    "Superficial peroneal",
    "Sural",
    "Deep peroneal",
    "Saphenous",
    "Medial plantar",
    "Lateral plantar",
    "LFCN",
  ],
};

const sensoryAmpHeader = "Amp(uV)";

const headers = [
  "Nerve",
  "Side",
  "Stim",
  "Rec",
  "Lat(ms)",
  sensoryAmpHeader,
  "Dur(ms)",
  "Dist(cm)",
  "CV(m/s)",
  "Note",
];

const SensoryTable: React.FC<SensoryTableProps> = ({
  rows,
  setRows,
  nerveOptions,
  recOptions,
  stimOptions,
  sideOptions,
}) => {
  const addStimRows = (fullLabel: string) => {
    if (!fullLabel.includes(" - ")) return;

    const [nervePart, stimWithRec] = fullLabel.split(" - ");
    const recMatch = stimWithRec.match(/^([^(]+)(?:\(([^)]+)\))?$/);
    if (!recMatch) return;

    const stimSite = recMatch[1].trim();
    const recSite = recMatch[2]?.trim();

    const matched = (NCS_REF as any).sensory_ncs.find((segment: any) => {
      return (
        segment.nerve === nervePart &&
        segment.stimulation_site === stimSite &&
        (!recSite || segment.recording_site === recSite)
      );
    });

    if (!matched) return;

    const newPair: SensoryRowType[] = sideOptions.map((side) => ({
      Nerve: matched.nerve,
      Side: side,
      Stim: matched.stimulation_site,
      Rec: matched.recording_site,
      "Lat(ms)": "",
      [sensoryAmpHeader]: "",
      "Dur(ms)": "",
      "Dist(cm)": "",
      "CV(m/s)": "",
      Note: "",
    })) as SensoryRowType[];

    setRows((prev) => {
      const updated = [...prev];
      let filled = 0;

      for (let index = 0; index < updated.length && filled < newPair.length; index++) {
        const row = updated[index];
        const isEmpty = !row.Nerve && !row.Stim && !row.Rec && !row.Side;
        if (isEmpty) {
          updated[index] = { ...updated[index], ...newPair[filled] };
          filled++;
        }
      }

      if (filled < newPair.length) {
        return [...updated, ...newPair.slice(filled)];
      }

      return updated;
    });
  };

  return (
    <section className="section-block">
      <div className="section-header">
        <h2 className="section-title">Sensory NCS</h2>

        <div className="toolbar">
          {["UEx", "LEx"].map((group) => (
            <select
              key={group}
              className="app-select compact-select"
              onChange={(event) => {
                addStimRows(event.target.value);
                event.target.value = "";
              }}
            >
              <option value="">{`+ Add Nerve (${group})`}</option>
              {(NCS_REF as any).sensory_ncs
                .filter((segment: any) => nerveGroups[group].includes(segment.nerve))
                .map((segment: any) => {
                  const label = isDuplicateLabel(NCS_REF, segment)
                    ? `${segment.nerve} - ${segment.stimulation_site} (${segment.recording_site})`
                    : `${segment.nerve} - ${segment.stimulation_site}`;

                  return (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  );
                })}
            </select>
          ))}
        </div>
      </div>

      <Table
        title=""
        headers={headers}
        nerveOptions={nerveOptions}
        sideOptions={sideOptions}
        stimOptions={stimOptions}
        recOptions={recOptions}
        muscleOptions={[]}
        rows={rows}
        setRows={setRows}
        dropdownOptions={{
          Note: [
            "Absent",
            "Prolonged",
            "Slow",
            "Decreased",
            "Prolonged, Slow",
            "Prolonged, Decreased",
            "Slow, Decreased",
            "Prolonged, Slow, Decreased",
          ],
        }}
      />
    </section>
  );
};

function isDuplicateLabel(ref: any, targetSeg: any): boolean {
  const matches = (ref.sensory_ncs as any[]).filter(
    (segment: any) =>
      segment.nerve === targetSeg.nerve &&
      segment.stimulation_site === targetSeg.stimulation_site
  );

  return matches.length > 1;
}

export default SensoryTable;
